import type { EmotionJSX } from '@emotion/react/types/jsx-namespace';
import {
  ImageComponent as Image,
  InnerHtml,
} from '@innovamat/glimmer-components';
import { useStretchesOfCourseQuery } from '@innovamat/glow-api-client';
import { usePrint } from '@innovamat/hooks';
import { Trans } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useEnvVariables } from '../../../env-variables-manager';
import { PrintFooter } from '../print-guides';
import type { Language } from './download-documents/select-options';
import { getPacingGuideFileName } from './get-file-name';
import {
  ActivityInfo,
  Bold,
  FileDescription,
  FileTitle,
  GlobalStyle,
  HeaderBar,
  Stretch,
  StyledTable,
  TabletTitle,
  TBodyCell,
  THeadCell,
  Wrapper,
} from './print-us-styled';
import { getTranslation } from './translations';

type Lesson = {
  title: string;
  type: string;
  duration: number;
  image: string;
  page?: string | null;
};

type ModulePacings = {
  title: string;
  lessons?: Lesson[] | null;
};

type StretchOfCourse = {
  title: string;
  description?: string | null;
  number: number;
  modulePacings: ModulePacings[];
};

const ContentTable = styled.table`
  width: 100%;
  margin-top: 24px;
  border-collapse: separate;
  border: none;
  border-spacing: 7px;
  table-layout: fixed;
`;

const THeadRow = styled.tr<{ hasReducedSize?: boolean }>`
  font-weight: 500;
  text-align: center;

  th {
    padding: 5px;
    border-radius: 4px;
    align-items: center;
    background-color: #f1f3f2;
    text-align: center;
    font-size: ${({ hasReducedSize }) => (hasReducedSize ? '12px' : '13px')};
    line-height: ${({ hasReducedSize }) => (hasReducedSize ? '14px' : '16px')};
    font-weight: 500;
  }
`;

const SpaceDescriptionCell = styled.th`
  width: 128px;
  color: #ffff;
  background-color: #b1bcbb !important;
`;

const TableCell = styled.td`
  border-radius: 4px;
  vertical-align: top;
`;

const LastDayTableCell = styled.td`
  border-radius: 4px;
  background-color: #d5f0f6;
  text-align: center;

  div {
    padding: 0px 10px;
  }
`;

const AssessmentImage = styled(Image)`
  object-position: bottom;
  width: 100% !important;
  height: 72px !important;
  background-color: #ffefcc;
  border-radius: 4px;
  object-fit: contain;
  flex-shrink: 0;
  position: relative !important;
`;

const ActivityImage = styled(Image)`
  width: 100% !important;
  height: auto !important;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  position: relative !important;
`;

const ImageContainer = styled.div`
  position: relative;
`;

const PageTag = styled.div`
  position: absolute;
  min-width: 38px;
  height: 14px;
  top: 4px;
  right: 4px;
  background-color: #f3f7f6;
  border-radius: 4px;
  padding: 0px 3px 1px 3px;
  font-size: 8px;
  font-weight: 600;
  line-height: 14px;
  color: #757575;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PageTagText = styled.span`
  transform: translateY(-1px);
`;

const RowHeader = styled(TableCell)<{ hasReducedSize?: boolean }>`
  font-size: ${({ hasReducedSize }) => (hasReducedSize ? '12px' : '13px')};
  line-height: ${({ hasReducedSize }) => (hasReducedSize ? '13px' : '14px')};
  height: 82px;
  display: flex;
  flex: 1 0 0;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  text-align: center;
  font-weight: 500;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const LessonTitle = styled.div<{ hasReducedSize?: boolean }>`
  font-size: ${({ hasReducedSize }) => (hasReducedSize ? '10px' : '12px')};
  line-height: ${({ hasReducedSize }) => (hasReducedSize ? '12px' : '16px')};
  height: 48px;
  width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
`;

const SpaceDescriptionText = styled.div`
  margin-top: 0;
  width: 128px;
  height: 117px;
  font-size: 8.78px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding: 4px;
  border-radius: 4px;
  border: 0.5px solid #b1bcbb;

  h3 {
    color: #718482;
    font-family: Roboto;
    font-size: 8px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-transform: uppercase;
  }

  h3:not(:first-of-type) {
    margin-top: 5px;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    position: relative;
    padding-left: 5px;
  }

  li::before {
    content: '•';
    position: absolute;
    left: 0;
    top: 0;
  }
`;

const TableHeader = ({
  hasSpaceDescriptions,
  language,
}: {
  hasSpaceDescriptions: boolean;
  language: Language;
}): EmotionJSX.Element => {
  return (
    <thead>
      <THeadRow hasReducedSize={hasSpaceDescriptions}>
        <th style={{ background: 'none', width: '28px' }} />

        {[...Array(5)].map((_, index) => {
          const day = index + 1;
          return (
            <THeadCell key={day}>
              {getTranslation(language, 'common.day')} {day}
            </THeadCell>
          );
        })}

        {hasSpaceDescriptions && (
          <SpaceDescriptionCell>
            {getTranslation(language, 'pacingGuide.modulesSpaces')}
          </SpaceDescriptionCell>
        )}
      </THeadRow>
    </thead>
  );
};

const LessonCell = ({
  duration,
  image,
  title,
  type,
  page,
  hasReducedSize,
}: Lesson & { hasReducedSize?: boolean }): EmotionJSX.Element => {
  const isAssessment = type.toLowerCase() === 'assessment';
  const isSpaceDescription = type.toLowerCase() === 'space descriptions';
  const { MANAGER_NEXT } = useEnvVariables();

  if (isSpaceDescription) {
    return (
      <TableCell>
        <SpaceDescriptionText>
          <InnerHtml text={title} />
        </SpaceDescriptionText>
      </TableCell>
    );
  }

  const imageProps = {
    src: image,
    alt: title,
    width: 256,
    height: 144,
    quality: 100,
    priority: true,
    sizes: '(max-width: 768px) 100vw, 256px',
  };

  return (
    <TableCell colSpan={duration}>
      <ImageContainer>
        {isAssessment ? (
          <AssessmentImage
            baseUrl={MANAGER_NEXT}
            hasOptimization
            {...imageProps}
          />
        ) : (
          <ActivityImage
            baseUrl={MANAGER_NEXT}
            hasOptimization
            {...imageProps}
          />
        )}
        {page && (
          <PageTag>
            <PageTagText>{page}</PageTagText>
          </PageTag>
        )}
      </ImageContainer>
      <LessonTitle hasReducedSize={hasReducedSize}>{title}</LessonTitle>
    </TableCell>
  );
};

const PrintStretch: React.FC<
  StretchOfCourse & { isLastStretch?: boolean; language: Language }
> = ({ title, description, modulePacings, isLastStretch, language }) => {
  const hasSpaceDescriptions = modulePacings.some(({ lessons }) =>
    lessons?.some(({ type }) => type.toLowerCase() === 'space descriptions')
  );

  const hasSharedTheFifthDayColumn = modulePacings
    .slice(0, 4)
    .every(
      (module) =>
        module?.lessons?.[4]?.type.toLowerCase() === 'digital practice'
    );

  const individualizedPracticeLesson =
    hasSharedTheFifthDayColumn && modulePacings[0]?.lessons?.[4];

  const sliceColumn = hasSharedTheFifthDayColumn ? 4 : 6;

  return (
    <StyledTable isLastItem={isLastStretch}>
      <thead>
        <tr>
          <THeadCell colSpan={1}>
            <HeaderBar className="header-bar">
              <FileTitle>
                {getTranslation(language, 'pacingGuide.title')}
              </FileTitle>
              <TabletTitle>
                <Stretch>{title}</Stretch>
              </TabletTitle>
            </HeaderBar>
          </THeadCell>
        </tr>
      </thead>
      <tbody>
        <tr>
          <TBodyCell>
            {description && (
              <FileDescription>
                <Trans
                  i18nKey={description}
                  components={{
                    b: <Bold />,
                    p: <p />,
                    br: <br />,
                    li: <li />,
                    ul: <ul />,
                  }}
                />
              </FileDescription>
            )}
            <ContentTable>
              <TableHeader
                hasSpaceDescriptions={hasSpaceDescriptions}
                language={language}
              />
              <tbody>
                {modulePacings.map((module, rowIndex) => (
                  <tr key={module.title}>
                    <RowHeader hasReducedSize={hasSpaceDescriptions}>
                      {module.title}
                    </RowHeader>
                    {(module.lessons ?? [])
                      .slice(0, sliceColumn)
                      .map(
                        (
                          { title, duration, image, type, page },
                          lessonIndex
                        ) => (
                          <LessonCell
                            key={`${title}-${lessonIndex}`}
                            title={title}
                            duration={duration}
                            image={image}
                            type={type}
                            page={page}
                            hasReducedSize={hasSpaceDescriptions}
                          />
                        )
                      )}

                    {hasSharedTheFifthDayColumn && rowIndex === 0 && (
                      <LastDayTableCell rowSpan={5}>
                        {individualizedPracticeLesson && (
                          <>
                            <ActivityImage
                              src={individualizedPracticeLesson.image}
                              alt={individualizedPracticeLesson.title}
                              width={256}
                              height={144}
                              quality={100}
                              sizes="(max-width: 768px) 100vw, 256px"
                            />
                            <ActivityInfo>
                              {individualizedPracticeLesson.title}
                            </ActivityInfo>
                          </>
                        )}
                      </LastDayTableCell>
                    )}
                  </tr>
                ))}
              </tbody>
            </ContentTable>
          </TBodyCell>
        </tr>
      </tbody>
    </StyledTable>
  );
};

export function PrintPacingGuide(): EmotionJSX.Element {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const stretch = searchParams.get('stretch');
  const noWrapper = searchParams.get('noWrapper') === 'true';
  const language = searchParams.get('language');
  const hideStandards = searchParams.get('hideStandards') === 'true';

  const { data } = useStretchesOfCourseQuery({
    courseUuid: courseId || '',
    stretch,
    language: language || 'en',
    hideStandards: hideStandards ? 'true' : 'false',
  });

  const { componentRef, loading, onPrint } = usePrint(
    getPacingGuideFileName({
      courseId: courseId || '',
      stretch,
      language: (language || 'en') as Language,
    })
  );

  const content = (
    <div ref={componentRef}>
      {data?.stretchesOfCourse.map((stretch, index) => (
        <PrintStretch
          key={index}
          {...stretch}
          isLastStretch={index === data.stretchesOfCourse.length - 1}
          language={(language || 'en') as Language}
        />
      ))}
    </div>
  );

  return (
    <>
      <GlobalStyle />
      {noWrapper ? (
        content
      ) : (
        <Wrapper>
          <PrintFooter onPrint={onPrint} loading={loading} />
          {content}
        </Wrapper>
      )}
    </>
  );
}
